/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  TableBody,
} from "@mui/material";
import FormTitle from "../../../form-fields/form-title";
import TextInput from "../../../form-fields/text-input";
import SelectInput from "../../../form-fields/select-input";
import DashBoardContainer from "../../../layout/dashboard-container";
import {
  FormLabel,
  FormValue,
  GridRow,
} from "../generate-receipt/custom-form-grid";
import { useSelector } from "react-redux";
import { labels } from "../../../../lang/labels";
import { useFormik, FormikProvider, Form } from "formik";
import { reprintReceiptValidationSchema } from "../../../../utils/validation-schema";
import { getReceiptList } from "../../../../services/generate-counter-receipt";
import { useNavigate } from "react-router-dom";
import { Print } from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FormButtons from "../buttons";
import useApiState from "../../common/useApiState";
import useApi from "../../../common/useApiHook";
import Loader from "../../../loader/loader";
import AlertMsg from "../../../common/alert";
import { getErrorMsg } from "../../../../utils/helpers";

const ReprintReceipt = () => {
  const navigate = useNavigate();
  const initialState = {
    propertyNumber: "",
    receiptType: "",
  };

  const [receiptTypes, setReceiptTypes] = useState([
    { value: "BillReceipt", label: "Bill Receipt" },
    { value: "PrefilledReceipt", label: "Prefilled Receipt" },
  ]);

  const [receiptData, setReceiptData] = useState("");
  const { loading, error, callApi, setError } = useApi(getReceiptList);

  const lang = useSelector((state) => state.userDetails.lang);
  const formik = useFormik({
    initialValues: initialState,
    validationSchema: reprintReceiptValidationSchema,
  });

  useEffect(() => {
    const getData = async (propertyDetails) => {
      try {
        await window.localStorage.removeItem("propertyDetails");
        const response = await getReceiptList(propertyDetails);
        setReceiptData(response);
      } catch (error) {
        setError(getErrorMsg(error));
      }
    };
    const propertyDetails = window.localStorage.getItem("propertyDetails");
    if (propertyDetails && receiptData === "") {
      const { propertyNumber, receiptType } = JSON.parse(propertyDetails);
      formik.setFieldValue("propertyNumber", propertyNumber);
      formik.setFieldValue("receiptType", receiptType);
      getData({ propertyNumber, receiptType });
    }else{
      window.localStorage.removeItem("propertyDetails");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitButtonClick = async (e = { preventDefault: () => {} }) => {
    e.preventDefault();
    if (formik.values.propertyNumber && formik.values.receiptType) {
      const response = await callApi(formik.values);
      setReceiptData(response);
    }
  };

  const handlePrintClick = (e, receipt) => {
    window.localStorage.setItem(
      "propertyDetails",
      JSON.stringify(formik.values)
    );
    e.preventDefault();
    navigate(
      `/PrintReceipt?receiptNo=${receipt.receiptno}&receiptType=${receipt.receiptType}`
    );
  };

  return (
    <DashBoardContainer>
      {loading && (
        <Loader
          message={`Loading Property Number: ${formik.values.propertyNumber}`}
        />
      )}
      {error && (
        <AlertMsg
          severity="error"
          onClose={() => {
            setError("");
          }}
        />
      )}
      <FormikProvider value={formik}>
        <Form>
          <Paper elevation={4} sx={{ marginBottom: "15px" }}>
            <FormTitle title={labels.Receipt[lang]} />
            <Grid>
              <GridRow>
                <FormLabel label={labels.PropertyNumber[lang]} required />
                <FormValue
                  component={<TextInput name="propertyNumber" required />}
                />
                <FormLabel label={labels.ReceiptType[lang]} required />
                <FormValue
                  component={
                    <SelectInput
                      name="receiptType"
                      options={receiptTypes}
                      required
                    />
                  }
                />
              </GridRow>
              <Grid container justifyContent="center" alignItems="center">
                <Grid
                  item
                  md={3}
                  container
                  justifyContent={{ md: "flex-end" }}
                  alignItems="center"
                  p={2}
                >
                  <FormButtons
                    isValid={!(formik.isValid && formik.dirty)}
                    handleSubmitButtonClick={handleSubmitButtonClick}
                    resetForm={() => {
                      formik.resetForm();
                      setReceiptData("");
                      setError("");
                    }}
                    submitBtnLabel="Show"
                  />
                </Grid>
              </Grid>
              {receiptData && (
                <GridRow>
                  <TableContainer style={{ padding: "50px" }}>
                    <Table
                      size="small"
                      sx={{ minWidth: 600 }}
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            background: "#e6eaeb",
                            "&:last-child td, &:last-child th": {
                              border: "1px solid black",
                            },
                          }}
                        >
                          <TableCell align="center">
                            {labels.PropertyNumber[lang]}
                          </TableCell>
                          <TableCell align="center">
                            {labels.PropertyName[lang]}
                          </TableCell>
                          <TableCell align="center">
                            {labels.ReceiptDate[lang]}
                          </TableCell>
                          <TableCell align="center">
                            {labels.ReceiptNumber[lang]}
                          </TableCell>
                          <TableCell align="center">
                            {labels.PaymentMode[lang]}
                          </TableCell>
                          <TableCell align="center">
                            {labels.Amount[lang]}
                          </TableCell>
                          <TableCell align="center">
                            {labels.Action[lang]}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {receiptData.map((receipt, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell align="center" className="cellBorder">
                                {receipt.propertyno}
                              </TableCell>
                              <TableCell align="center" className="cellBorder">
                                {receipt.propertyname}
                              </TableCell>
                              <TableCell align="center" className="cellBorder">
                                {receipt.receiptdate}
                              </TableCell>
                              <TableCell align="center" className="cellBorder">
                                {receipt.receiptno}
                              </TableCell>
                              <TableCell align="center" className="cellBorder">
                                {receipt.modeofpayment}
                              </TableCell>
                              <TableCell align="center" className="cellBorder">
                                {receipt.paidamount}
                              </TableCell>
                              <TableCell align="center" className="cellBorder">
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={(e) => {
                                    handlePrintClick(e, receipt);
                                  }}
                                  disabled={!receipt?.receiptno}
                                  startIcon={<Print />}
                                >
                                  Print
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </GridRow>
              )}
            </Grid>
          </Paper>
        </Form>
      </FormikProvider>
    </DashBoardContainer>
  );
};
export default ReprintReceipt;
